import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePostApiCompaniesCurrentServicemodules,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceModuleType } from '@/api/model/serviceModuleType'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { notify } from '@/lib/notify'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth.store'
import type { ServiceModuleModel } from '@/api/model'
import { SERVICE_MODULE_TYPES } from '../grid-components/ServiceModuleCard'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Passed along as the parent folder — for the (upcoming) folder navigation. */
  parentFolderServiceModuleId?: number | null
  /** The freshly created module, so the page can open the editor right away. */
  onCreated: (module: ServiceModuleModel) => void
}

export function CreateServiceModuleDialog({ open, onOpenChange, parentFolderServiceModuleId, onCreated }: Props) {
  const { t } = useTranslation(['service-modules', 'common'])
  const [selectedType, setSelectedType] = useState<ServiceModuleType | null>(null)
  const queryClient = useQueryClient()
  const create = usePostApiCompaniesCurrentServicemodules()
  // Several types may carry a flag, so read the list once instead of one useFeatureFlag per type.
  const features = useAuthStore((s) => s.features)

  const handleOpenChange = (next: boolean) => {
    if (!next) setSelectedType(null)
    onOpenChange(next)
  }

  async function handleCreate() {
    if (!selectedType) return
    try {
      const res = await create.mutateAsync({
        data: { serviceModuleType: selectedType, parentFolderServiceModuleId: parentFolderServiceModuleId ?? null },
      })
      const created = ((res as any)?.data ?? res) as ServiceModuleModel
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
      notify.success(t('toasts.created'))
      setSelectedType(null)
      onOpenChange(false)
      // Straight into the editor so the module can be named/configured.
      if (created?.id) onCreated(created)
    } catch (error) {
      notify.apiError(error, t('toasts.createError'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('createDialog.title')}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 py-2">
          {SERVICE_MODULE_TYPES.filter((e) => !e.featureFlag || features?.includes(e.featureFlag)).map(({ value, Icon, defaultColor }) => (
            <button
              key={value}
              onClick={() => setSelectedType(value)}
              className={cn(
                'flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all text-center',
                selectedType === value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
              )}
            >
               <div className="size-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: defaultColor }}>
                <Icon className="size-5 text-white" />
              </div>
              <span className="text-xs font-medium leading-tight">{t(`types.${value}`)}</span>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button disabled={!selectedType || create.isPending} onClick={handleCreate}>{t('createDialog.create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
